import React from 'react';
import LoginModal from '../Login/LoginModal';
import PaymentMethodModal from '../PaymentMethodModal/PaymentMethodModal';
import RmbPaymentBar from './RmbPaymentBar';
import './PricingPlans.scss';
import './PricingPlansCard.scss';

const sortPricesByOrder = (prices) => {
  if (!Array.isArray(prices)) return [];
  return [...prices].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
};
const formatMoney = (amount, currency) => {
  const normalizedCurrency = (currency || 'USD').toUpperCase();
  const parsed = typeof amount === 'number' ? amount : Number(amount);
  const formatted = Number.isFinite(parsed) ? parsed.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : String(amount ?? '');
  const symbolMap = { USD: '$', CNY: '¥', EUR: '€', GBP: '£', JPY: '¥', HKD: 'HK$' };
  const symbol = symbolMap[normalizedCurrency];
  if (symbol) return `${symbol}${formatted}`;
  if (!normalizedCurrency) return formatted;
  return `${normalizedCurrency} ${formatted}`;
};
const getPrimaryPlanPrice = (plan) => {
  if (!plan) return undefined;
  if (plan.price != null) return plan.price;
  const first = sortPricesByOrder(plan.prices)[0];
  return first?.price;
};
const PricingPlansView = ({
  t,
  containerClass,
  language,
  showHeader,
  showBillingToggle,
  loading,
  error,
  billingCycle,
  setBillingCycle,
  currentPlans,
  handlePlanClick,
  authShowLoginModal,
  handleCloseLogin,
  handleLoginSuccess,
  showPaymentModal,
  setShowPaymentModal,
  selectedPlan,
  setSelectedPlan,
  selectedBillingCycle,
  setSelectedBillingCycle,
  handlePaymentMethodSelect,
  handleRmbPaymentClick,
  getButtonText
}) => {
  if (loading) {
    return (
      <div className={`pricing-plans-component ${containerClass}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('pricing.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`pricing-plans-component ${containerClass}`}>
        <div className="error-container">
          <div className="error-message">
            <h3>{t('pricing.unableToLoadPlans')}</h3>
            <p>{error}</p>
            <button 
              className="retry-button" 
              onClick={() => window.location.reload()}
            >
              {t('pricing.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasPlans = currentPlans && currentPlans.length > 0;
  if (!hasPlans) {
    return (
      <div className={`pricing-plans-component ${containerClass}`}>
        {showHeader && (
          <div className="pricing-header">
            <h1>{t('pricing.chooseYourPlan')}</h1>
            <p>{t('pricing.selectPerfectPlan')}</p>
          </div>
        )}
        
        {showBillingToggle && (
          <div className="billing-toggle">
            <button 
              className={billingCycle === 'yearly' ? 'active' : ''}
              onClick={() => setBillingCycle('yearly')}
            >
              {t('pricing.yearly')}
            </button>
            <button 
              className={billingCycle === 'monthly' ? 'active' : ''}
              onClick={() => setBillingCycle('monthly')}
            >
              {t('pricing.monthly')}
            </button>
            <button 
              className={billingCycle === 'credits' ? 'active' : ''}
              onClick={() => setBillingCycle('credits')}
            >
              {t('pricing.creditPacks')}
            </button>
          </div>
        )}
        
        <div className="no-plans-container">
          <div className="no-plans-message">
            <h3>{t('pricing.noPlansAvailable')}</h3>
            <p>{t('pricing.noPlansMessage', { billingCycle })}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`pricing-plans-component ${containerClass}`}>
      {showHeader && (
        <div className="pricing-header">
          <h1>{t('pricing.chooseYourPlan')}</h1>
          <p>{t('pricing.selectPerfectPlan')}</p>
        </div>
      )}
      
      {showBillingToggle && (
        <div className="billing-toggle">
          <button 
            className={billingCycle === 'yearly' ? 'active' : ''}
            onClick={() => setBillingCycle('yearly')}
          >
            {t('pricing.yearly')}
          </button>
          <button 
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
          >
            {t('pricing.monthly')}
          </button>
          <button 
            className={billingCycle === 'credits' ? 'active' : ''}
            onClick={() => setBillingCycle('credits')}
          >
            {t('pricing.creditPacks')}
          </button>
        </div>
      )}

      <div className="pricing-plans">
        {currentPlans.map((plan) => {
          const sortedPrices = sortPricesByOrder(plan?.prices);
          const displayPrices =
            sortedPrices.length > 0
              ? sortedPrices
              : [
                  {
                    id: `${plan?.id || 'plan'}-single`,
                    currency: 'USD',
                    price: plan?.price,
                    originalPrice: plan?.originalPrice,
                    discountPercentage: plan?.discountPercentage
                  }
                ];

          const effectivePrices = displayPrices.filter((p) => p && p.price != null);
          const hasCnyPrice = effectivePrices.some(p => p.currency === 'CNY');

          return (
            <div key={plan.id} className={`plan-item-container ${plan.isRecommended ? 'popular' : ''}`}>
              {plan.isRecommended && <div className="popular-badge">{t('pricing.mostPopular')}</div>}

              <div className="pricing-plan-header">
                <h3>{plan.name}</h3>
                <p className="plan-description">{plan.subtitle}</p>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: effectivePrices.length === 1 ? 'center' : 'flex-start',
                  gap: 6,
                  paddingLeft: effectivePrices.length === 1 ? 0 : 16
                }}>
                  {effectivePrices.map((p, index) => {
                    const isSecondary = index > 0;
                    const discountPercentage = (p.discountPercentage != null && p.discountPercentage > 0)
                      ? p.discountPercentage
                      : (p.originalPrice && p.price
                        ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100)
                        : 0);
                    const showDiscount = index === effectivePrices.length - 1 && discountPercentage > 0;

                    return (
                      <div
                        key={p.id || `${plan.id}-${index}`}
                        className="plan-price"
                        style={{ marginBottom: index === effectivePrices.length - 1 ? 8 : 0 }}
                      >
                         <div className="price-row">
                            {/* Original Price */}
                            {p.originalPrice != null && (
                              <span className="original-price">{formatMoney(p.originalPrice, p.currency)}</span>
                            )}

                            {/* Current Price */}
                            <span className={isSecondary ? "price-secondary" : "price"}>
                              {formatMoney(p.price, p.currency)}
                            </span>
                         </div>
                        
                        {/* Discount Badge */}
                        {showDiscount && (
                           <span className="discount-badge">
                             <span className="fire-icon">🔥</span> 
                             {language === 'zh-CN' ?  `节省${discountPercentage}%` : language === 'zh-TW' 
                               ? `節省${discountPercentage}%` 
                               : `Save ${discountPercentage}%`
                             }
                           </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            <div className="pricing-plan-features">
              <ul>
                {plan.description && plan.description.split('\n').map((feature, index) => {
                  const text = feature.trim().replace(/^[•·-]\s*/, '');
                  if (!text) return null;
                  return (
                    <li key={index}>
                      <span className="checkmark">✓</span>
                      {text}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              {language === 'zh-TW' && billingCycle === 'credits' && hasCnyPrice && (
                <RmbPaymentBar onPay={() => handleRmbPaymentClick(plan)} />
              )}

              <button
                className={`plan-button ${plan.isRecommended ? 'popular' : ''}`}
                onClick={() => handlePlanClick(plan)}
              >
                {getButtonText(plan, billingCycle)}
              </button>
            </div>
          </div>
          );
        })}
      </div>
      
      <LoginModal 
        isOpen={authShowLoginModal}
        onClose={handleCloseLogin}
        onLoginSuccess={handleLoginSuccess}
      />
      
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedPlan(null);
          setSelectedBillingCycle('yearly');
        }}
        onSelectPaymentMethod={handlePaymentMethodSelect}
        planName={selectedPlan?.name}
        planPrice={getPrimaryPlanPrice(selectedPlan)}
        billingCycle={selectedBillingCycle}
        isSubscription={selectedBillingCycle !== 'credits'}
        planId={selectedPlan?.id}
      />
    </div>
  );
};

export default PricingPlansView;
